import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

/**
 * Figma "Button" (01 · Foundations). Pill, Label/Large. Disabled is a prop on any variant rather than a
 * fifth variant. `destructive` exists in the design system, but sensitive actions (suspend, delete,
 * revoke, publish) use primary/secondary plus a confirm dialog - see KT §5.4.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'brand' | 'ghost' | 'destructive';
export type ButtonSize = 'primary' | 'compact' | 'small' | 'row' | 'review' | 'state' | 'dialog';

const BASE =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap select-none rounded-(--mbm-component-radius-button) ' +
  'transition-opacity duration-(--mbm-duration-fast) hover:opacity-(--mbm-opacity-pressed) ' +
  'focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring) ' +
  'disabled:cursor-not-allowed disabled:bg-action-disabled disabled:bg-none disabled:text-text-disabled disabled:shadow-none disabled:hover:opacity-100';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-action-primary text-text-on-dark',
  secondary: 'bg-action-secondary text-text-primary shadow-(--mbm-shadow-sm)',
  brand: 'bg-brand-primary bg-(image:--mbm-gradient-hero) text-text-on-dark',
  ghost: 'bg-transparent text-text-primary',
  destructive: 'bg-action-destructive text-text-on-dark',
};

const SIZE: Record<ButtonSize, string> = {
  primary: 'h-(--mbm-size-button-primary) px-(--mbm-space-6) text-label-large',
  compact: 'h-(--mbm-size-button-compact) px-(--mbm-space-4) text-label-large',
  small: 'h-(--mbm-size-button-small) px-(--mbm-space-4) text-label-medium',
  /** Trailing action in a settings row (Figma A16). */
  row: 'h-(--mbm-size-button-row) px-(--mbm-space-4) text-label-medium',
  /** The client's answer to a version (Figma C05.2 "Your review"): 52 tall, side by side. */
  review: 'h-(--mbm-size-button-review) min-w-0 px-(--mbm-space-5) text-label-large',
  /** The one action under an empty / error / 403 / 404 state (Figma P01): 40 tall, 24 across. */
  state: 'h-(--mbm-size-button-row) px-(--mbm-space-6) text-label-large',
  /** The two buttons of a confirm or session dialog (Figma P01): 44 tall, 24 across. */
  dialog: 'h-(--mbm-size-button-dialog) px-(--mbm-space-6) text-label-large',
};

export interface ButtonStyleOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
}

/** Class string for non-<button> elements, e.g. `<Link className={buttonClassName({ variant: 'secondary' })}>`. */
export function buttonClassName({ variant = 'primary', size = 'primary', fullWidth = false }: ButtonStyleOptions = {}) {
  return cx(BASE, VARIANT[variant], SIZE[size], fullWidth && 'w-full');
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonStyleOptions {
  /** Hidden by default, matching the Figma component's "Leading icon: display none" slot. */
  icon?: PhosphorIcon;
  /** Blocks further clicks and announces busy state while a request is in flight. */
  loading?: boolean;
  children?: ReactNode;
}

export function Button({
  variant,
  size,
  fullWidth,
  icon,
  loading = false,
  disabled,
  type = 'button',
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(buttonClassName({ variant, size, fullWidth }), className)}
      {...props}
    >
      {icon ? <Icon icon={icon} size="md" /> : null}
      {children}
    </button>
  );
}
